import React, {
  memo, useState, useEffect, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import HttpUtil from '../../../../../utils/http-util';
import useTranslation from '../../../../../utils/translation-utils';
import FileUploadError from './file-upload-failure';
import FileUploadSuccess from './file-upload-success';

const UploadedFileSection = ({
  file, fetchConfig, onDelete, onFileUploadSuccess, autoUpload, onFileUploadFailure, showAPIError,
  apiErrors, setErrorMessages, allFilesUploaded, updateFileName, fileIndex, isDialogOpen, type,
  fileErrors, maxSize, openFileUploadWindow, allowFileNameEditing,
}) => {
  const [getTranslation] = useTranslation();
  const [progress, setProgress] = useState();

  const handleDelete = useCallback(() => {
    onDelete(file);
    setErrorMessages([]);
  }, [file, onDelete]);

  useEffect(() => {
    if (fetchConfig) {
      if (autoUpload) {
        const { url, options = {}, formData = {} } = fetchConfig;
        const config = {
          onUploadProgress(progressEvent) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setProgress(percentCompleted);
          },
          ...options,
        };
        const requestData = new FormData();
        requestData.append('file', file);
        Object.keys(formData).map((key) => requestData.append(key, formData[key]));
        HttpUtil.post(url, requestData, {}, config).then((uploadResponse) => {
          const { status, messages } = uploadResponse;
          if (!status) {
            setErrorMessages(messages.map(({ message }) => message));
          }
          onFileUploadSuccess(uploadResponse);
        }).catch((error) => {
          setErrorMessages([getTranslation('UPLOAD_FAILED_MESSAGE')]);
          onFileUploadFailure(error);
        });
      }
    } else {
      setProgress(100);
    }
  }, [autoUpload, file]);

  const getView = () => {
    if ((showAPIError && apiErrors.length > 0) || fileErrors.length > 0) {
      if (allFilesUploaded && fileErrors.length === 0 && apiErrors.length === 0) {
        return null;
      }
      return (
        <FileUploadError
          maxSize={maxSize}
          fileName={file.name}
          apiErrors={apiErrors}
          onDelete={handleDelete}
          fileErrors={fileErrors}
          isDialogOpen={isDialogOpen}
          setErrorMessages={setErrorMessages}
          allFilesUploaded={allFilesUploaded}
        />
      );
    }

    return (
      <FileUploadSuccess
        type={type}
        file={file}
        progress={progress}
        fileIndex={fileIndex}
        onDelete={handleDelete}
        autoUpload={autoUpload}
        updateFileName={updateFileName}
        allowFileNameEditing={allowFileNameEditing}
        openFileUploadWindow={openFileUploadWindow}
      />
    );
  };

  return (
    getView()
  );
};

UploadedFileSection.defaultProps = {
  ...FileUploadSuccess.defaultProps,
  maxSize: 0,
  apiErrors: [],
  fileErrors: [],
  autoUpload: true,
  fetchConfig: null,
  showAPIError: true,
  isDialogOpen: false,
  allFilesUploaded: false,
  setErrorMessages: () => {},
  allowFileNameEditing: true,
  onFileUploadSuccess: () => {},
  onFileUploadFailure: () => {},
  openFileUploadWindow: () => {},
};

UploadedFileSection.propTypes = {
  ...FileUploadSuccess.propTypes,
  maxSize: PropTypes.number,
  autoUpload: PropTypes.bool,
  showAPIError: PropTypes.bool,
  isDialogOpen: PropTypes.bool,
  setErrorMessages: PropTypes.func,
  allFilesUploaded: PropTypes.bool,
  onFileUploadSuccess: PropTypes.func,
  onFileUploadFailure: PropTypes.func,
  allowFileNameEditing: PropTypes.bool,
  openFileUploadWindow: PropTypes.func,
  apiErrors: PropTypes.arrayOf(PropTypes.string),
  fileErrors: PropTypes.arrayOf(PropTypes.shape({})),
  file: PropTypes.shape({ name: PropTypes.string }).isRequired,
  fetchConfig: PropTypes.shape({ formData: PropTypes.shape({}), url: PropTypes.string }),
};

export default memo(UploadedFileSection);
